import { ErrorIndicator } from 'components/Shared/ErrorIndicator'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'
import NoPatronsCheckedInIcon from 'assets/NoPatronsCheckedIn.png'
import { NoDataIndicator } from 'components/Shared/NoDataIndicator'
import { RewardRedemptionsTable } from './RewardRedemptionsTable'
import { PlayerPaybackReportsHeader } from '../PlayerPaybackReportsHeader'
import { useState } from 'react'
import { Grid, Stack, styled } from '@mui/material'
import { useSetLe } from 'stores/useSetLe'
import { getLEIdsByPermission } from 'utils/permissions'
import { DatePicker } from '@mui/x-date-pickers'
import { SectionHeader } from 'components/SectionHeader'
import { Colors, colorPrimitives } from 'components/Theme'
import { isValid } from 'date-fns'
import { useSetMe } from 'stores/useMeQuery'

const StyledDatePicker = styled(DatePicker)({
  '& .MuiFormLabel-root': {
    fontSize: '1rem',
    fontWeight: '100',
    color: Colors.muiLabels.placeholder,
  },
  '& .Mui-focused': {
    color: !Colors.muiLabels.placeholder,
  },
})

const getDefaultStartDate = () => {
  const date = new Date()
  date.setDate(date.getDate() - 30)
  date.setHours(0, 0, 0, 0) // set to midnight 30 days ago
  return date
}

const getDefaultEndDate = () => {
  const date = new Date()
  date.setHours(23, 59, 59, 999)
  return date
}

export const RewardRedemptionsPage = () => {
  const { passedLe } = useSetLe()
  const { passedMe } = useSetMe()
  const meQuery = passedMe
  const selectedCorporateAccountId =
    useCurrentCorporateAccountStore().currentCorporateAccountId
  const licensedEstablishments = meQuery.data?.licensedEstablishments?.filter(
    (le) => le.corporateAccountId === selectedCorporateAccountId
  )

  const leData =
    meQuery?.data?.user?.licensedEstablishments?.find(
      (x) =>
        x.licensedEstablishmentId === Number(passedLe?.id) &&
        meQuery?.data?.licensedEstablishments?.find(
          (x) => x.corporateAccountId === selectedCorporateAccountId
        )?.id
    ) ?? undefined

  const piiPermission = leData?.reportsAccessToPIIPermission
  const downloadPIIPermission = leData?.reportsDownloadPIIPermission

  const [startDate, setStartDate] = useState<Date>(getDefaultStartDate())
  const [endDate, setEndDate] = useState<Date>(getDefaultEndDate())

  // Get LE Ids for the permission the user has access to
  const leIdsByPermission: number[] = getLEIdsByPermission(
    meQuery.data,
    selectedCorporateAccountId,
    'playerPaybackReportsPermission'
  )

  return (
    <PlayerPaybackReportsHeader currentTab={'3'}>
      {!licensedEstablishments || licensedEstablishments.length === 0 ? (
        <NoDataIndicator
          imageSource={NoPatronsCheckedInIcon}
          noDataMessage="Cannot view reports because there are no locations available for this corporate account"
        />
      ) : (
        leIdsByPermission.length !== 0 && (
          <Stack spacing={4}>
            <SectionHeader title="Redemptions" showButton={false} />
            <Grid
              container
              className="flex flex-column"
              rowGap={2}
              columnGap={3}
            >
              <StyledDatePicker
                disabled={Number(passedLe?.id) === 0}
                disableFuture
                onChange={(date: any) => {
                  if (isValid(date)) {
                    setStartDate(date.setHours(0, 0, 0, 0))
                  }
                }}
                label="Start Date"
                value={startDate}
                format="MM/dd/yyyy"
              />
              <StyledDatePicker
                disabled={Number(passedLe?.id) === 0}
                onChange={(date: any) => {
                  if (isValid(date)) {
                    setEndDate(date.setHours(23, 59, 59, 0))
                  }
                }}
                label="End Date"
                value={endDate}
                format="MM/dd/yyyy"
              />
            </Grid>
            {startDate > endDate && (
              <ErrorIndicator
                color={colorPrimitives.darkRed}
                message={'Start Date must be before End Date'}
              />
            )}
            <RewardRedemptionsTable
              startDate={startDate}
              endDate={endDate}
              piiPermission={piiPermission ?? false}
              downloadPIIPermission={downloadPIIPermission ?? false}
            />
          </Stack>
        )
      )}
    </PlayerPaybackReportsHeader>
  )
}
